import React, { createContext, useContext, useEffect, useState } from 'react';
import { noop } from 'lodash/fp';
import { useHistory } from 'react-router-dom';
import { PRODUCTS_PER_PAGE } from '../../constants';
import { getUrlParams, setUrlParams } from '../../../../utils/helpers/urlParams';

interface ContextProps {
    query: string;
    setQuery: (arg: string) => void;
    categories: string[];
    setCategories(arg: string[]): void;
    rowsPerPage: number;
    setRowsPerPage(arg: number): void;
    page: number;
    setPage(arg: number): void;
    markupSort: string | false;
    foodCostSort: string | false;
    setMarkupSort(arg: string | false): void;
    setFoodCostSort(arg: string | false): void;
}

type InitialValues = Partial<Pick<ContextProps, 'markupSort' | 'foodCostSort' | 'query' | 'categories' | 'rowsPerPage' | 'page'>>;

const defaultState: ContextProps = {
    markupSort: false,
    foodCostSort: false,
    query: '',
    categories: [] as string[],
    rowsPerPage: PRODUCTS_PER_PAGE,
    page: 0,
    setQuery: noop,
    setCategories: noop,
    setRowsPerPage: noop,
    setPage: noop,
    setMarkupSort: noop,
    setFoodCostSort: noop,
};

const QueryContext = createContext<ContextProps>(defaultState);

export const ProductQueryContextProvider: React.FC = ({ children }) => {
    const history = useHistory();
    const [markupSortParam, foodCostSortParam, queryParam, categoriesParam, rowsPerPageParam, pageParam] = getUrlParams({
        searchString: history.location.search,
        params: ['markupSort', 'foodCostSort', 'query', 'categories', 'rowsPerPage', 'page']
    });
    const query = queryParam !== null ? queryParam : defaultState.query;
    const categories = categoriesParam ? categoriesParam.split(',') : defaultState.categories;
    const rowsPerPage = rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : defaultState.rowsPerPage;
    const page = pageParam ? parseInt(pageParam, 10) : defaultState.page;
    const markupSort = markupSortParam || '';
    const foodCostSort = foodCostSortParam || '';

    useEffect(() => {
        const params = setUrlParams(history.location.search, {
            query,
            categories,
            rowsPerPage,
            page,
            markupSort,
            foodCostSort,
        });

        history.push({ search: params.toString() });
        // eslint-disable-next-line
    }, []);

    const context = useProvideContext({
        query,
        categories,
        rowsPerPage,
        page,
        markupSort,
        foodCostSort,
    });

    return <QueryContext.Provider value={context}>{children}</QueryContext.Provider>;
};

export const useProductQueryContext = () => {
    return useContext(QueryContext);
};

function useProvideContext(initialValues: InitialValues) {
    const [page, setPage] = useState<ContextProps['page']>(initialValues.page || defaultState.page);

    const [rowsPerPage, setRowsPerPage] = useState<ContextProps['rowsPerPage']>(initialValues.rowsPerPage || defaultState.rowsPerPage);
    const [query, setQuery] = useState<ContextProps['query']>(initialValues.query || defaultState.query);
    const [categories, setCategories] = useState<ContextProps['categories']>(initialValues.categories || defaultState.categories);
    const [foodCostSort, setFoodCostSort] = useState<ContextProps['foodCostSort']>(initialValues.foodCostSort || defaultState.foodCostSort);
    const [markupSort, setMarkupSort] = useState<ContextProps['markupSort']>(initialValues.markupSort || defaultState.markupSort);
    return {
        page, setPage,
        rowsPerPage, setRowsPerPage,
        query, setQuery,
        categories, setCategories,
        foodCostSort, setFoodCostSort,
        markupSort, setMarkupSort,
    };
}
